import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize SizeDialog to prevent unnecessary re-renders
const SizeDialog = React.memo(({ open, onClose, canvas, selectedObject }) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [prevWidth, setPrevWidth] = useState(0);
  const [prevHeight, setPrevHeight] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [inputWidth, setInputWidth] = useState("");
  const [inputHeight, setInputHeight] = useState("");
  const selectedObjectRef = useRef(selectedObject);

  // Update ref when selectedObject changes
  useEffect(() => {
    selectedObjectRef.current = selectedObject;
    console.log("[SizeDialog] selectedObject updated:", selectedObject);
  }, [selectedObject]);

  // Initialize size values when dialog opens or selected object changes
  useEffect(() => {
    if (selectedObject && open) {
      const currentWidth = Math.round(Number(selectedObject.get("width")) || 0);
      const currentHeight = Math.round(
        Number(selectedObject.get("height")) || 0
      );
      setWidth(currentWidth);
      setHeight(currentHeight);
      setInputWidth(currentWidth.toString());
      setInputHeight(currentHeight.toString());
      setPrevWidth(currentWidth);
      setPrevHeight(currentHeight);
      setIsDirty(false);
      console.log(
        "[SizeDialog] Initialized width:",
        currentWidth,
        "height:",
        currentHeight
      );
    }
  }, [selectedObject, open]);

  // Memoize update callbacks
  const updateWidthCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setWidth(roundedValue);
      setInputWidth(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ width: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[SizeDialog] Updated width:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[SizeDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  const updateHeightCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setHeight(roundedValue);
      setInputHeight(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ height: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[SizeDialog] Updated height:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[SizeDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  // Create debounced update functions
  const updateWidth = useDebouncedCallback(updateWidthCallback, 100, canvas);
  const updateHeight = useDebouncedCallback(updateHeightCallback, 100, canvas);

  // Handle dialog close (cancel)
  const handleCancel = useCallback(() => {
    if (isDirty && selectedObject) {
      selectedObject.set({ width: prevWidth, height: prevHeight });
      selectedObject.setCoords();
      canvas?.requestRenderAll();
      console.log(
        "[SizeDialog] Canceled, reverted to width:",
        prevWidth,
        "height:",
        prevHeight
      );
    }
    onClose();
  }, [isDirty, selectedObject, prevWidth, prevHeight, canvas, onClose]);

  // Handle apply size (save changes and add to history)
  const handleApply = useCallback(() => {
    updateWidth.flush();
    updateHeight.flush();
    if (isDirty && canvas && selectedObject) {
      canvas.setActiveObject(selectedObject);
      selectedObject.set({ width, height });
      selectedObject.setCoords();
      canvas.requestRenderAll();
      canvas.fire("object:modified", { target: selectedObject });
      console.log(
        "[SizeDialog] Applied width:",
        width,
        "height:",
        height,
        "Fired object:modified for:",
        selectedObject
      );
    } else {
      console.log(
        "[SizeDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvas,
        "selectedObject=",
        !!selectedObject
      );
    }
    setIsDirty(false);
    onClose();
  }, [
    isDirty,
    canvas,
    selectedObject,
    width,
    height,
    updateWidth,
    updateHeight,
    onClose,
  ]);

  // Stop mouse events from propagating to allow input interaction
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent backdrop click from closing the dialog
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent keydown events (e.g., Escape key) from closing the dialog
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  // Handle input changes
  const handleWidthChange = useCallback((e) => {
    setInputWidth(e.target.value);
  }, []);

  const handleWidthBlur = useCallback(() => {
    const value = Number(inputWidth);
    if (!isNaN(value) && value >= 0) {
      updateWidth(value);
    } else {
      setInputWidth(width.toString());
    }
  }, [inputWidth, width, updateWidth]);

  const handleHeightChange = useCallback((e) => {
    setInputHeight(e.target.value);
  }, []);

  const handleHeightBlur = useCallback(() => {
    const value = Number(inputHeight);
    if (!isNaN(value) && value >= 0) {
      updateHeight(value);
    } else {
      setInputHeight(height.toString());
    }
  }, [inputHeight, height, updateHeight]);

  const handleWidthStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateWidth(value);
      }
    },
    [updateWidth]
  );

  const handleHeightStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateHeight(value);
      }
    },
    [updateHeight]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Size Toolbar
      </DialogTitle>
      <DialogContent
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Stack direction="column" sx={{ marginTop: 1 }}>
          <TextField
            type="number"
            id="canvasWidth"
            margin="dense"
            value={inputWidth}
            onChange={handleWidthChange}
            onBlur={handleWidthBlur}
            onInput={handleWidthStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1px for fine control
            }}
            label="Width"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Width in pixels" }}
          />
          <TextField
            type="number"
            id="canvasHeight"
            margin="dense"
            value={inputHeight}
            onChange={handleHeightChange}
            onBlur={handleHeightBlur}
            onInput={handleHeightStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1px for fine control
            }}
            label="Height"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Height in pixels" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel size changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply size changes"
        >
          Apply Size
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SizeDialog;
